import type { DictsysGender, DictsysShortGender } from "@/types";

type GenderMapKey = "Common" | "Feminine" | "Masculine" | "Neutral";

interface GenderMapValue {
  long: DictsysGender[];
  short: DictsysShortGender;
}

type GenderMap = Partial<Record<GenderMapKey, GenderMapValue>>;

export const LATIN_GENDER_MAP: GenderMap = {
  Feminine: { long: ["femininum", "hunkøn"], short: "f." },
  Masculine: { long: ["maskulinum", "hankøn"], short: "m." },
  Neutral: { long: ["neutrum", "intetkøn"], short: "n." }
};

export const NORDIC_GENDER_MAP: GenderMap = {
  Common: { long: ["fælleskøn"], short: "en" },
  Neutral: { long: ["intetkøn"], short: "et" }
};

type ExtendedGender = {
  gender: DictsysGender;
  mapKey: GenderMapKey;
};

export class ArticleGenders {
  readonly genders: ExtendedGender[];
  readonly genderMap: GenderMap;

  constructor(
    readonly dictsysGenders: DictsysGender[],
    genderMap: GenderMap
  ) {
    this.genderMap = genderMap;
    this.genders = dictsysGenders
      .map((gender): ExtendedGender | undefined => {
        switch (true) {
          case this.#isCommon(gender):
            return { gender, mapKey: "Common" };
          case this.#isFeminine(gender):
            return { gender, mapKey: "Feminine" };
          case this.#isMasculine(gender):
            return { gender, mapKey: "Masculine" };
          case this.#isNeutral(gender):
            return { gender, mapKey: "Neutral" };
        }
      })
      .filter((gender): gender is ExtendedGender => gender !== undefined);
  }

  getFull(): DictsysGender[][] {
    return this.genders
      .map((gender) => this.genderMap[gender.mapKey]?.long)
      .filter((gender): gender is DictsysGender[] => gender !== undefined);
  }

  getShort(): DictsysShortGender[] {
    return this.genders
      .map((gender) => this.genderMap[gender.mapKey]?.short)
      .filter((gender): gender is DictsysShortGender => gender !== undefined);
  }

  #isCommon(gender: DictsysGender): boolean {
    return Boolean(this.genderMap.Common?.long.includes(gender));
  }

  #isFeminine(gender: DictsysGender): boolean {
    return Boolean(this.genderMap.Feminine?.long.includes(gender));
  }

  #isMasculine(gender: DictsysGender): boolean {
    return Boolean(this.genderMap.Masculine?.long.includes(gender));
  }

  #isNeutral(gender: DictsysGender): boolean {
    return Boolean(this.genderMap.Neutral?.long.includes(gender));
  }
}
